import { useRef, type FC } from 'react';
import { createRefetchContainer, graphql, type RelayRefetchProp } from 'react-relay/legacy.js';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { hasPersonalizedFilterQueryParam } from '../../shared/helpers/personalizedFiltersHelpers.js';
import { handleSbUserCountryCode } from '../helpers/handleSbUserCountryCode.ts';
import { type RefetchFn } from '../types/layoutRefetch.ts';

/* Components */
import { handlePageRefetchVariableOnUserChange } from '../../shared/handlePageRefetchVariableOnUserChange.ts';
import { SbSharedRefetchContainer } from '../SbSharedRefetchContainer/SbSharedRefetchContainer.jsx';
import { SbRespDesignsComponents } from '../SbRespDesignsComponents/SbRespDesignsComponents.jsx';
import { SbSharedHead } from '../SbSharedHead/SbSharedHead.tsx';
import {
    RecentlyViewedItemsProvider,
    TABLET,
    DESKTOP,
} from 'dibs-recent-history/exports/RecentlyViewedItemsProvider.ts';
import { SbSharedFavoritesNudgeToast } from '../../shared/SharedFavoritesNudge/SbSharedFavoritesNudgeToast/SbSharedFavoritesNudgeToast.tsx';

/* Hooks */
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useSearchCorrections } from '../useSearchCorrections.ts';
import { useSharedUserAndRelayVarsLoader } from '../../shared/SharedUserAndRelayVarLoader.tsx';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { useSbSharedPageTracking } from '../hooks/useSbSharedPageTracking.ts';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap.ts';

import { type SbRespDesignsLayout_viewer$data } from './__generated__/SbRespDesignsLayout_viewer.graphql.ts';
import { type SbRespDesignsLayoutRefetchQuery$variables } from './__generated__/SbRespDesignsLayoutRefetchQuery.graphql.ts';

type RefetchVariables = Partial<
    SbRespDesignsLayoutRefetchQuery$variables & { fetchSbOnUserChange: boolean }
>;

type Props = {
    viewer: SbRespDesignsLayout_viewer$data;
    reinitTracking?: () => void;
    relay: RelayRefetchProp;
    runNotificationQueue?: () => void;
};

const SbRespDesignsLayoutComponent: FC<Props> = ({
    viewer,
    reinitTracking,
    relay,
    runNotificationQueue = () => {},
}) => {
    const relayVars = useRef<RefetchVariables>({});
    const { isTablet, isItemFavoritingFeb2026Variant, isItemFavoritingFeb2026Control } =
        useServerVarsContext();
    const { itemSearch, regionalInfo = null, shippingFilters = null, user = null } = viewer;
    const userId = user?.serviceId ?? '';

    useDwelledOnPage(PAGE_TYPE.SEARCH);

    const doRefetch: RefetchFn<RefetchVariables> = (variables, renderVariables, callback) => {
        relayVars.current = {
            ...relayVars.current,
            ...variables,
            userCountryCode: handleSbUserCountryCode({
                userCountryCode: variables?.userCountryCode,
                fetchSb: variables?.fetchSb,
            }),
        };
        relay.refetch(
            relayVars.current,
            { ...relayVars.current, ...renderVariables, fetchSb: true },
            callback
        );
    };

    const onUserRefetch: RefetchFn<RefetchVariables> = (variables, renderVariables, callback) => {
        const { isClient, fetchSbOnUserChange } = variables || {};
        const hasPersonalizedFilter = isClient && hasPersonalizedFilterQueryParam();
        //Personal-rerank needs to refetch s&b after login
        const fetchSb = handlePageRefetchVariableOnUserChange(variables) || fetchSbOnUserChange;

        doRefetch(
            {
                ...variables,
                fetchSb,
                skipUser: false,
                hasPersonalizedFilter,
            },
            renderVariables,
            callback
        );
    };

    const handleSbRefetch: RefetchFn<RefetchVariables> = (variables, renderVariables, callback) => {
        doRefetch({ ...variables, fetchSb: true }, renderVariables, () => {
            callback();

            runNotificationQueue();
        });
    };

    useSharedUserAndRelayVarsLoader({ onUserRefetch });
    const [searchCorrectionTerm] = useSearchCorrections({ itemSearch });
    useSbSharedPageTracking({
        itemSearch,
        user,
        reinitTracking,
        refineMenuFiltersMap: sbRespRefineMenuFiltersMap,
        searchCorrectionTerm,
    });

    return (
        <RecentlyViewedItemsProvider deviceType={isTablet ? TABLET : DESKTOP}>
            <SbSharedHead
                itemSearch={itemSearch}
                imageFallback={itemSearch.browseContentModule?.mainImage}
            />
            <SbSharedRefetchContainer
                viewer={viewer}
                itemSearch={itemSearch}
                onSbRefetch={handleSbRefetch}
                regionalInfo={regionalInfo}
                shippingFilters={shippingFilters}
                shouldShowSpinner={false}
            />
            <SbRespDesignsComponents
                viewer={viewer}
                itemSearch={itemSearch}
                user={user}
                isClient={!!relayVars.current.isClient}
                shippingFilters={shippingFilters}
            />
            <SbSharedFavoritesNudgeToast
                userId={userId}
                isItemFavoritingFeb2026Variant={isItemFavoritingFeb2026Variant}
                isItemFavoritingFeb2026Control={isItemFavoritingFeb2026Control}
            />
        </RecentlyViewedItemsProvider>
    );
};

export const SbRespDesignsLayout = createRefetchContainer(
    SbRespDesignsLayoutComponent,
    {
        viewer: graphql`
            fragment SbRespDesignsLayout_viewer on Viewer
            @argumentDefinitions(
                hasPersonalizedFilter: { type: "Boolean", defaultValue: false }
                userZipCode: { type: "String", defaultValue: "" }
                fetchRegionalInfo: { type: "Boolean", defaultValue: false }
            ) {
                ...SbRespDesignsComponents_viewer
                itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    originalUrl: $originalUrl
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                    disableNonParameterizedUrlRedirects: $disableNonParameterizedUrlRedirects
                    userCountryCode: $userCountryCode
                    includeSponsoredItems: $includeSponsoredItems
                    userHasConfirmedOrders: $userHasConfirmedOrders
                    priceBookName: $priceBookName
                    numOfItemsToInflate: $numOfItemsToInflate
                ) @required(action: THROW) {
                    ...useSearchCorrections_itemSearch
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbRespDesignsComponents_itemSearch @include(if: $fetchSb)
                    ...SbSharedHead_itemSearch
                    ...useSbSharedPageTracking_itemSearch
                    browseContentModule(contentModuleId: $contentModuleId) {
                        ... on ContentModuleIconicDesign {
                            mainImage
                        }
                    }
                }
                user(userId: $userId) @include(if: $fetchUser) {
                    serviceId
                    ...useSbSharedPageTracking_user
                    ...SbRespDesignsComponents_user
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(
                    uriRef: $uriRef
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespDesignsComponents_shippingFilters
                }
            }
        `,
    },
    graphql`
        query SbRespDesignsLayoutRefetchQuery(
            $userId: String = ""
            $personalizationId: String = ""
            $guestId: String = ""
            $fetchUser: Boolean!
            $fetchSb: Boolean!
            $skipUser: Boolean = true
            $isTrade: Boolean!
            $uriRef: String
            $originalUrl: String
            $first: Int!
            $page: Int!
            $localeOrigin: String = ""
            $showSeller: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
            $contentModuleId: String = ""
            $regions: String = ""
            $hasPersonalizedFilter: Boolean!
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $disableForceFacet: Boolean = true
            $isClient: Boolean!
            $disableNonParameterizedUrlRedirects: Boolean = false # Only used for static SB
            $fetchTileVideo: Boolean = false
            $priceBookName: String
            $includeSponsoredItems: Boolean = false
            $userHasConfirmedOrders: Boolean = false
            $pageDisplayEnum: PageDisplayEnum = searchAndBrowse
            $numOfItemsToInflate: Int
        ) {
            viewer {
                ...SbRespDesignsComponents_viewer
                itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    originalUrl: $originalUrl
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                    disableNonParameterizedUrlRedirects: $disableNonParameterizedUrlRedirects
                    userCountryCode: $userCountryCode
                    includeSponsoredItems: $includeSponsoredItems
                    userHasConfirmedOrders: $userHasConfirmedOrders
                    priceBookName: $priceBookName
                    numOfItemsToInflate: $numOfItemsToInflate
                ) @include(if: $fetchSb) {
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbRespDesignsComponents_itemSearch
                    ...SbSharedHead_itemSearch
                    ...useSbSharedPageTracking_itemSearch
                }
                user(userId: $userId) @include(if: $fetchUser) @skip(if: $skipUser) {
                    ...useSbSharedPageTracking_user
                    ...SbRespDesignsComponents_user
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(
                    uriRef: $uriRef
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespDesignsComponents_shippingFilters
                }
            }
        }
    `
);
